// Admin data layer for Families. RLS scopes everything to the admin's org.

import { supabase, isSupabaseConfigured } from "../supabase";

function ensureLive() {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
}

// ---------------------------------------------------------------------------
// List + Detail
// ---------------------------------------------------------------------------

export async function listFamilies({ search } = {}) {
  ensureLive();
  // Fetch families with all data needed for the table + KPIs in one go.
  // Schedule fields are wrapped in a try/fallback so the page keeps
  // rendering when the Module 42 migrations haven't been applied yet.
  const baseChildren =
    "id, first_name, last_name, classroom_id, withdrawn_at";
  const fullChildren = `${baseChildren}, schedule_pattern, tuition_multiplier`;
  const buildSelect = (childCols) => `
      id, display_name, primary_contact_profile_id, created_at,
      members:family_members(id, profile_id, relationship, is_primary_contact,
        profile:profiles(id, full_name, email)),
      children:children(${childCols}),
      billing_profile:billing_profiles(
        id, billing_email, status, autopay_status,
        invoices:invoices(id, status, amount_total_cents)
      )
    `;

  const tryFetch = async (childCols) => {
    let q = supabase
      .from("families")
      .select(buildSelect(childCols))
      .order("display_name", { ascending: true });
    if (search) q = q.ilike("display_name", `%${search}%`);
    return q;
  };

  let { data, error } = await tryFetch(fullChildren);
  if (error && /column .* does not exist/i.test(error.message || "")) {
    ({ data, error } = await tryFetch(baseChildren));
  }
  if (error) throw error;
  return (data || []).map(decorateFamilyRow);
}

export async function getFamily(familyId) {
  ensureLive();
  const { data, error } = await supabase
    .from("families")
    .select(`
      id, organization_id, display_name, primary_contact_profile_id, created_at,
      members:family_members(id, profile_id, relationship, is_primary_contact, can_pickup,
        profile:profiles(id, full_name, email, phone)),
      children:children(id, first_name, last_name, date_of_birth, classroom_id, withdrawn_at,
        classroom:classrooms(id, name, age_group)),
      billing_profile:billing_profiles(id, billing_email, status, autopay_status)
    `)
    .eq("id", familyId)
    .maybeSingle();
  if (error) throw error;
  return data ? decorateFamilyRow(data) : null;
}

function decorateFamilyRow(row) {
  const activeChildren = (row.children || []).filter((c) => !c.withdrawn_at);
  const bp = Array.isArray(row.billing_profile)
    ? row.billing_profile[0]
    : row.billing_profile;
  const invoices = bp?.invoices || [];
  const openBalance = invoices
    .filter((i) => i.status === "issued" || i.status === "failed")
    .reduce((a, i) => a + (i.amount_total_cents || 0), 0);
  const primaryMember =
    (row.members || []).find((m) => m.profile_id === row.primary_contact_profile_id) ||
    (row.members || []).find((m) => m.is_primary_contact) ||
    (row.members || [])[0] ||
    null;
  return {
    ...row,
    billing_profile: bp || null,
    member_count: (row.members || []).length,
    child_count: (row.children || []).length,
    active_child_count: activeChildren.length,
    schedule_mix: computeScheduleMix(activeChildren),
    open_balance_cents: openBalance,
    primary_contact_email: primaryMember?.profile?.email || bp?.billing_email || null,
    primary_contact_name: primaryMember?.profile?.full_name || null,
  };
}

// ---------------------------------------------------------------------------
// Schedule mix — calm operational signal on the admin family roster.
// Buckets active children by schedule pattern. Returns a small summary
// admins can scan at a glance without opening the family detail.
// ---------------------------------------------------------------------------
export function computeScheduleMix(activeChildren) {
  const buckets = { full_time: 0, part_time: 0, custom: 0 };
  for (const c of activeChildren || []) {
    const pattern = c.schedule_pattern || "full_time";
    if (pattern === "full_time")          buckets.full_time += 1;
    else if (pattern === "custom")        buckets.custom    += 1;
    else /* part_time_*day */             buckets.part_time += 1;
  }
  const total = activeChildren?.length || 0;
  // Mixed = any combination of full + part. Pure = all in one bucket.
  const populated = Object.values(buckets).filter((n) => n > 0).length;
  const isMixed = populated > 1;
  const allFullTime = total > 0 && buckets.full_time === total;
  return {
    total,
    full_time: buckets.full_time,
    part_time: buckets.part_time,
    custom:    buckets.custom,
    is_mixed:  isMixed,
    all_full_time: allFullTime,
  };
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export async function createFamily({ organizationId, displayName, primaryContactProfileId }) {
  ensureLive();
  const { data, error } = await supabase
    .from("families")
    .insert({
      organization_id: organizationId,
      display_name: displayName,
      primary_contact_profile_id: primaryContactProfileId || null,
    })
    .select("*")
    .single();
  if (error) throw error;
  return data;
}

export async function updateFamily(id, patch) {
  ensureLive();
  const { data, error } = await supabase
    .from("families")
    .update(patch)
    .eq("id", id)
    .select("*")
    .single();
  if (error) throw error;
  return data;
}

/**
 * Safe delete: block if children (even withdrawn) or billing activity exists.
 * Returns { ok:true } or { ok:false, reason, details }.
 * Uses .select() on the delete to detect RLS-blocked zero-row cases,
 * and translates foreign-key errors (23503) into the same reason codes
 * so the UI can always show a friendly toast.
 */
export async function deleteFamily(id) {
  ensureLive();
  const [children, billing] = await Promise.all([
    supabase.from("children").select("id").eq("family_id", id).limit(1),
    supabase.from("billing_profiles").select("id").eq("family_id", id).limit(1),
  ]);
  if (children.error) throw children.error;
  if (billing.error) throw billing.error;
  if ((children.data || []).length > 0) {
    return { ok: false, reason: "has_children" };
  }
  if ((billing.data || []).length > 0) {
    return { ok: false, reason: "has_billing" };
  }
  const { data, error } = await supabase
    .from("families")
    .delete()
    .eq("id", id)
    .select("id");
  if (error) {
    if (error.code === "23503") {
      // Foreign-key reference survived the pre-check (race condition or
      // hidden-by-RLS dependency). Surface as a friendly reason.
      return { ok: false, reason: "has_children" };
    }
    throw error;
  }
  if (!data || data.length === 0) {
    return { ok: false, reason: "blocked" };
  }
  return { ok: true };
}

// ---------------------------------------------------------------------------
// Family members
// ---------------------------------------------------------------------------

export async function findProfileByEmail(email) {
  ensureLive();
  const { data, error } = await supabase.rpc("rpc_find_profile_by_email", {
    p_email: email,
  });
  if (error) throw error;
  // Supabase returns null OR a row; RETURNS <table> becomes either an object or null.
  if (!data) return null;
  if (Array.isArray(data)) return data[0] || null;
  if (!data.id) return null;
  return data;
}

export async function addFamilyMember({ familyId, profileId, relationship, isPrimaryContact }) {
  ensureLive();
  const { data, error } = await supabase
    .from("family_members")
    .insert({
      family_id: familyId,
      profile_id: profileId,
      relationship: relationship || null,
      is_primary_contact: !!isPrimaryContact,
    })
    .select("*")
    .single();
  if (error) throw error;
  if (isPrimaryContact) {
    // Bump family.primary_contact_profile_id for convenience / UI reads.
    await supabase
      .from("families")
      .update({ primary_contact_profile_id: profileId })
      .eq("id", familyId);
  }
  return data;
}

export async function removeFamilyMember(memberId) {
  ensureLive();
  const { error } = await supabase.from("family_members").delete().eq("id", memberId);
  if (error) throw error;
}
